import { type FC, useRef, useEffect, useCallback, type ComponentType } from 'react'
import { Capacitor } from '@capacitor/core'
import { type AssistantMessageProps, type MessagesProps } from '@copilotkit/react-ui'
import { Alert, AlertType, PageHeaderRedesign } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import './styles.css'
import cn from 'classnames'
import { useCopilotChat } from '@copilotkit/react-core'
import { AssistantMessage } from '../assistant-message/assistant-message'

interface CustomMessagesProps extends Omit<MessagesProps, 'RenderMessage'> {
  setIsChatOpen: (_isChatOpen: boolean) => void
  isShowBackButton?: boolean
  RenderMessage?: FC<{
    message: unknown
    inProgress: boolean
    index: number
    isCurrentMessage: boolean
    AssistantMessage?: ComponentType<AssistantMessageProps>
  }>
  error: string | Error | null
}

export const Messages: FC<CustomMessagesProps> = ({
  messages,
  inProgress,
  setIsChatOpen,
  isShowBackButton = true,
  RenderMessage,
  error,
}) => {
  const { t } = useTranslation('features-chat')
  const wrapperStyles = cn('p-sm w-full overflow-y-auto flex-1')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isUserScrolledUpRef = useRef(false)
  const isProgrammaticScrollRef = useRef(false)
  const { reset } = useCopilotChat()

  const AssistantMessageComponent = useCallback(
    (props: any) => <AssistantMessage {...props} isLastMessage={props.isCurrentMessage} />,
    [],
  )

  const scrollToLastUserMessage = useCallback(() => {
    if (wrapperRef.current && messages.length > 0) {
      isProgrammaticScrollRef.current = true

      const userMessageElements = wrapperRef.current.querySelectorAll('[data-message][data-role="user"]')

      if (userMessageElements.length > 0) {
        const lastUserMessage = userMessageElements[userMessageElements.length - 1]

        lastUserMessage.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight
      }
    }
  }, [messages.length])

  const handleScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false
      return
    }
    if (wrapperRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = wrapperRef.current
      isUserScrolledUpRef.current = scrollTop + clientHeight < scrollHeight - 10
    }
  }, [])

  // Track user scroll
  useEffect(() => {
    const container = wrapperRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  // Track user messages to detect new user input
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userMessageCount = messages.filter((m: any) => m?.role === 'user').length
  const prevUserMessageCountRef = useRef(0)

  useEffect(() => {
    // Only reset scroll state when user actually sends a NEW message
    if (userMessageCount > prevUserMessageCountRef.current) {
      isUserScrolledUpRef.current = false
      scrollToLastUserMessage()
    }
    prevUserMessageCountRef.current = userMessageCount
  }, [userMessageCount, scrollToLastUserMessage])

  // Auto-scroll when inProgress changes
  useEffect(() => {
    if (inProgress) {
      // Only scroll when AI starts responding if user hasn't scrolled up
      if (!isUserScrolledUpRef.current) {
        scrollToLastUserMessage()
      }
    } else {
      // Only scroll when AI finishes if user hasn't scrolled up
      if (!isUserScrolledUpRef.current) {
        scrollToLastUserMessage()
      }
    }
  }, [inProgress, scrollToLastUserMessage])

  return (
    <div ref={wrapperRef} className={wrapperStyles}>
      <div className="max-w-[752px] mx-auto w-full">
        {!Capacitor.isNativePlatform() ? (
          <PageHeaderRedesign
            title={t('chat.title')}
            className="md:mb-xl hidden lg:block typo-primary-3xl-regular text-text-body !w-full !px-0 !max-w-[752px]"
            containerClassName="items-center"
            titleClassName="!mt-0"
            data-testid="chat-header"
            {...(isShowBackButton && {
              onClickBackButton: () => {
                reset()
                setIsChatOpen(false)
              },
            })}
          />
        ) : null}
        {messages.map((message, index) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const messageRole = (message as any)?.role || 'user'
          return RenderMessage ? (
            <div key={message.id} data-message data-role={messageRole}>
              <RenderMessage
                message={message}
                inProgress={inProgress}
                index={index}
                isCurrentMessage={index === messages.length - 1}
                AssistantMessage={AssistantMessageComponent}
              />
            </div>
          ) : (
            <div key={message.id} data-message data-role={messageRole}>
              {/* Fallback message rendering */}
              <div className="py-2x">
                <p>{JSON.stringify(message)}</p>
              </div>
            </div>
          )
        })}
        {/* Render error message if exists */}
        {error ? (
          <Alert alertType={AlertType.Info} className="max-w-full md:max-w-[340px] mx-auto mt-lg" description={t('common.error')} />
        ) : null}
      </div>
    </div>
  )
}
